// src/features/auth/components/RegisterForm.tsx
//
// Sign-up, trimmed to what a first visit can bear: a provider button or
// email + password. Name is optional (derived from the email when blank),
// there is no "confirm password" (the field has a show/hide toggle instead)
// and agreement to the terms is stated under the button rather than gated
// behind a checkbox. Recordings showed visitors abandoning the previous
// six-field form within seconds.
import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, Paper, Alert, Link, Stack, Chip } from '@mui/material';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { registerUser } from '../api/authApi';
import useAuthStore from '../../../store/authStore';
import clarityService from '../../../services/clarityService';
import { SocialLoginButtons } from '../../../components/auth';
import PasswordField from '../../../components/auth/PasswordField';
import { OAuthError } from '../../../types/oauth';
import { User } from '../../../services/authService';
import { brand } from '../../../config/brand';
import { getAppCallback, redirectToApp, resolveRedirect, splitName } from '../utils/redirectToApp';

const PERKS = ['100,000 credit points to start', 'No card needed', 'Every AI model included'];

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useAuthStore();

  // Desktop/Electron app OAuth flow
  const appCallback = getAppCallback(location.search);
  const redirectTo = resolveRedirect(location.state, location.search);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState({ email: '', password: '' });
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const markStarted = () => {
    if (started) return;
    setStarted(true);
    clarityService.track('register_start');
  };

  const finish = (user: User) => {
    if (redirectToApp(appCallback, user)) return;
    navigate(redirectTo, { replace: true });
  };

  // Handle OAuth signup success
  const handleOAuthSuccess = (user: User, isNewUser?: boolean) => {
    if (isNewUser) {
      clarityService.track('oauth_signup_success');
      clarityService.upgrade('New User OAuth Registration');
    } else {
      clarityService.track('oauth_login_success', 'register_page');
    }
    finish(user);
  };

  // Handle OAuth signup error
  const handleOAuthError = (err: OAuthError) => {
    clarityService.track('oauth_failed', 'register');
    setOauthError(err.message);
  };

  // Validate form
  const validateForm = (): boolean => {
    let valid = true;
    const newErrors = { email: '', password: '' };

    if (!email) {
      newErrors.email = 'Enter your email address';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'That does not look like an email address';
      valid = false;
    }

    if (!password) {
      newErrors.password = 'Choose a password';
      valid = false;
    } else if (password.length < 8) {
      newErrors.password = 'Use at least 8 characters';
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  // Handle registration submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const { firstName, lastName } = splitName(name, email);
      const user = await registerUser({ firstName, lastName, email, password });

      clarityService.track('register_success');
      clarityService.upgrade('New User Registration');
      finish(user);
    } catch (err) {
      clarityService.track('register_failed');
      // Error is handled in the API layer and stored in the auth store
      console.error('Registration failed:', err);
    }
  };

  const loginHref = `/login${appCallback ? `?app_callback=${encodeURIComponent(appCallback)}` : ''}`;

  return (
    <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 440, width: '100%' }}>
      <Typography variant="h4" component="h1" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
        Create your free account
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        {`Takes about a minute. Then download ${brand.productName} and start delegating.`}
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {PERKS.map((perk) => (
          <Chip
            key={perk}
            size="small"
            icon={<CheckIcon sx={{ fontSize: 16 }} />}
            label={perk}
            variant="outlined"
            color="success"
          />
        ))}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <SocialLoginButtons
        onSuccess={handleOAuthSuccess}
        onError={handleOAuthError}
        disabled={isLoading}
        context="signup"
        showDivider={false}
        error={oauthError}
      />

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1.5 }}>
          or sign up with email
        </Typography>

        <TextField
          margin="dense"
          fullWidth
          id="name"
          label="Your name"
          name="name"
          autoComplete="name"
          placeholder="Optional"
          value={name}
          onFocus={markStarted}
          onChange={(e) => setName(e.target.value)}
          disabled={isLoading}
        />

        <TextField
          margin="dense"
          required
          fullWidth
          id="email"
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onFocus={markStarted}
          onChange={(e) => setEmail(e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          disabled={isLoading}
        />

        <PasswordField
          margin="dense"
          required
          fullWidth
          name="password"
          label="Password"
          id="password"
          autoComplete="new-password"
          value={password}
          onFocus={markStarted}
          onChange={(e) => setPassword(e.target.value)}
          error={!!formErrors.password}
          helperText={formErrors.password || 'At least 8 characters'}
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 2, mb: 1.5, fontWeight: 700 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Create account'}
        </Button>

        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mb: 2 }}>
          By creating an account you agree to the{' '}
          <Link component={RouterLink} to="/terms" target="_blank">
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link component={RouterLink} to="/privacy" target="_blank">
            Privacy Policy
          </Link>
          .
        </Typography>

        <Box textAlign="center">
          <RouterLink to={loginHref} state={location.state} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              Already have an account? Sign in
            </Typography>
          </RouterLink>
        </Box>
      </Box>
    </Paper>
  );
};

export default RegisterForm;
